<template>
  <VaButton
    :preset="vuesticPreset"
    v-bind="vuesticColorBinding"
    :size="vuesticSize"
    :disabled="disabled"
    :type="type"
    :data-variant="variant"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabelledby"
    @click="onClick"
  >
    <template v-if="$slots['icon-left']" #prepend>
      <slot name="icon-left" />
    </template>
    <slot />
    <template v-if="$slots['icon-right']" #append>
      <slot name="icon-right" />
    </template>
  </VaButton>
</template>

<script lang="ts">
/**
 * Public-facing variant / size / type enums.
 *
 * These const-records live here (the canonical `App*` implementation) rather
 * than in `BaseButton.vue`. The previous layout had `AppButton` runtime-import
 * the enums from `BaseButton` while `BaseButton` runtime-imported `AppButton`
 * — a circular module dependency that hit "Cannot access 'ButtonVariant'
 * before initialization" at `withDefaults` time, because `BaseButton`'s
 * `<script setup>` was still mid-load when `AppButton` referenced the
 * binding. Re-exporting from `@/shared/ui` keeps the existing 25+ consumers
 * (`import { ButtonVariant, ButtonSize, ButtonType } from '@/shared/ui'`)
 * working unchanged.
 */
export const ButtonVariant = {
  Primary: 'primary',
  Secondary: 'secondary',
  Ghost: 'ghost',
  Danger: 'danger',
} as const

export type ButtonVariant = (typeof ButtonVariant)[keyof typeof ButtonVariant]

export const ButtonSize = {
  Sm: 'sm',
  Md: 'md',
} as const

export type ButtonSize = (typeof ButtonSize)[keyof typeof ButtonSize]

export const ButtonType = {
  Button: 'button',
  Submit: 'submit',
  Reset: 'reset',
} as const

export type ButtonType = (typeof ButtonType)[keyof typeof ButtonType]
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { VaButton } from 'vuestic-ui'

export interface AppButtonProps {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  readonly disabled?: boolean
  readonly type?: ButtonType
  readonly ariaLabel?: string
  readonly ariaLabelledby?: string
}

const props = withDefaults(defineProps<AppButtonProps>(), {
  variant: ButtonVariant.Primary,
  size: ButtonSize.Md,
  disabled: false,
  type: ButtonType.Button,
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

/**
 * Map our four-variant API onto Vuestic's `preset` + `color` pair.
 *
 *   primary   → 'primary'  + default color (the token-bridged `--va-primary`)
 *   secondary → 'secondary'
 *   ghost     → 'plain'    (Vuestic's no-fill surface)
 *   danger    → 'primary'  + explicit `color="danger"` so the danger token wins
 */
const vuesticPreset = computed<'primary' | 'secondary' | 'plain'>(() => {
  switch (props.variant) {
    case ButtonVariant.Secondary:
      return 'secondary'
    case ButtonVariant.Ghost:
      return 'plain'
    case ButtonVariant.Danger:
    case ButtonVariant.Primary:
    default:
      return 'primary'
  }
})

/**
 * `exactOptionalPropertyTypes: true` rejects a `:color="string | undefined"`
 * because Vuestic's `color` prop is `ColorName` (no `undefined`). Build a
 * spreadable object that omits `color` entirely on the non-danger path so
 * Vuestic falls back to its own bridge default.
 */
const vuesticColorBinding = computed<{ color?: string }>(() =>
  props.variant === ButtonVariant.Danger ? { color: 'danger' } : {},
)

const vuesticSize = computed<'small' | 'medium'>(() =>
  props.size === ButtonSize.Sm ? 'small' : 'medium',
)

function onClick(event: MouseEvent): void {
  emit('click', event)
}
</script>
